import type { AnnouncementKey, User } from "@sunner-cloud/api-client";
import mixpanel from "mixpanel-browser";

import { normalizeEventPath } from "./mixpanelNormalize";

// Estado real de inicialización. Gatear las llamadas por la presencia del token
// no basta: en tests el token puede venir de `.env` (lo carga Vite) sin que
// `initMixpanel` se haya ejecutado, y llamar a Mixpanel sin init lanza
// (p. ej. reset() accede al recorder inexistente). El flag es la única guarda
// correcta: solo se pone a true cuando init() se ha llamado de verdad.
let initialized = false;
// Último usuario identificado, para no repetir identify/people.set en cada
// refetch de `useUser` (devuelve otro objeto aunque el user_id no cambie).
let identifiedUserId: string | null = null;

export const initMixpanel = () => {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;
  if (initialized || !token) {
    return;
  }

  mixpanel.init(token, {
    api_host: "https://api-eu.mixpanel.com",
    autocapture: true,
    record_sessions_percent: 100,
    // Session Replay enmascara todo el texto por defecto; lo mostramos porque
    // es una herramienta interna. Los inputs siguen enmascarados
    // (record_mask_all_inputs sigue en true) para no grabar contraseñas.
    record_mask_all_text: false,
    persistence: "localStorage",
    debug: import.meta.env.DEV,
    hooks: { before_track: normalizeEventPath },
  });
  initialized = true;
};

export const identifyMixpanelUser = (user: User) => {
  if (!initialized || user.user_id === identifiedUserId) {
    return;
  }

  // Cambio de identidad sin un reset() previo: la sesión anterior pudo
  // limpiarse por expiración de token (rootRoute.isAuthenticated o el
  // initializer de AuthProvider borran el localStorage sin pasar por logout,
  // que es el único sitio que llama a resetMixpanel). Sin reset, identificar
  // al nuevo usuario fusionaría su device_id con el del saliente en un
  // dispositivo compartido. Resetear aquí cubre todos esos paths.
  if (identifiedUserId !== null) {
    mixpanel.reset();
  }

  identifiedUserId = user.user_id;
  mixpanel.identify(user.user_id);
  mixpanel.people.set({
    job_function: user.job_function ?? "none",
    company: user.company_name,
  });
};

// Sin importar `DevicesView` de `features/devices`: `config` es una capa "shared" y
// no puede depender de una "feature" (boundaries/dependencies). El tipo se repite en
// línea; el llamador sigue tipado por `DevicesView`, estructuralmente compatible.
export const trackDevicesViewShown = (view: "table" | "layout") => {
  if (!initialized) {
    return;
  }

  mixpanel.track("devices_view_shown", { view });
};

export const trackPlantOnboardingShown = () => {
  if (!initialized) {
    return;
  }

  mixpanel.track("plant_onboarding_shown");
};

export const trackPlantDashboardViewed = (
  period: "today" | "7d" | "custom",
) => {
  if (!initialized) {
    return;
  }

  mixpanel.track("plant_dashboard_viewed", { period });
};

export const trackAlertsPageViewed = (
  source: "sidebar" | "email" | "popup",
) => {
  if (!initialized) {
    return;
  }

  mixpanel.track("alerts_page_viewed", { source });
};

export const trackAlertsEmailClicked = (cta: "diagnose" | "configure") => {
  if (!initialized) {
    return;
  }

  mixpanel.track("alerts_email_clicked", { cta });
};

export const trackAlertsConfigStarted = () => {
  if (!initialized) {
    return;
  }

  mixpanel.track("alerts_config_started");
};

export const trackAlertsActivated = (properties: {
  threshold: number;
  delay_minutes: number;
  plants_count: number;
  recovery_enabled: boolean;
}) => {
  if (!initialized) {
    return;
  }

  mixpanel.track("alerts_activated", properties);
};

export const trackAlertsDeactivated = (properties: { days_active: number }) => {
  if (!initialized) {
    return;
  }

  mixpanel.track("alerts_deactivated", properties);
};

export const trackAnnouncementShown = (key: AnnouncementKey) => {
  if (!initialized) {
    return;
  }

  mixpanel.track("announcement_shown", { key });
};

export const trackAnnouncementCtaClicked = (key: AnnouncementKey) => {
  if (!initialized) {
    return;
  }

  mixpanel.track("announcement_cta_clicked", { key });
};

export const trackAnnouncementDismissed = (key: AnnouncementKey) => {
  if (!initialized) {
    return;
  }

  mixpanel.track("announcement_dismissed", { key });
};

export const resetMixpanel = () => {
  if (!initialized) {
    return;
  }

  identifiedUserId = null;
  mixpanel.reset();
};
